// ============================================================================
// OBT — Rol paneli özet verisi
// ============================================================================
// Projede bir server-state kütüphanesi (react-query vb.) YOKTUR; mevcut
// `{data, loading, error, refresh}` hook deseni (src/hooks/useTeams.js,
// useRoles.js) izlenir.
//
// F1'de gösterilen TÜM sayılar gerçek backend yanıtından gelir. Sahte sayaç,
// örnek kayıt veya sabit demo verisi üretilmez; veri yoksa boş durum gösterilir.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchObtCampaigns, OBT_QUEUE_FETCHERS } from './obtApi';
import { normalizeObtError } from './obtErrors';

const CAMPAIGN_PAGE_SIZE = 20;
// Panel özeti arka planda tazelenir (bildirim ziliyle aynı ritim): OBT sekmesinde
// beklerken yeni talep/kuyruk değişimi manuel "Yenile" GEREKTİRMEDEN yansır.
const POLL_MS = 45_000;

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

/**
 * Rol paneli için kampanya özeti ve (varsa) inceleme kuyruğu sayısı.
 *
 * @param {string|null} dashboardKind 'director' | 'deputy' | 'engineer' | 'foreman'
 * @param {{enabled?: boolean}} options `enabled=false` iken hiç istek atılmaz
 *   (yetki kapısı henüz karar vermemişken gereksiz 403 üretmemek için).
 */
export function useObtOverview(dashboardKind, { enabled = true } = {}) {
  const [state, setState] = useState({
    loading: enabled,
    error: null,
    campaigns: [],
    page: null,
    queueItems: null,
  });
  const abortRef = useRef(null);

  // `silent`: arka plan yoklaması — spinner GÖSTERME (panel titremesin). İlk
  // yükleme ve manuel "Yenile" silent=false.
  const load = useCallback(
    async (silent = false) => {
      if (!enabled || !dashboardKind) {
        setState({
          loading: false,
          error: null,
          campaigns: [],
          page: null,
          queueItems: null,
        });
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!silent) setState((prev) => ({ ...prev, loading: true, error: null }));

      const queueFetcher = OBT_QUEUE_FETCHERS[dashboardKind] || null;

      try {
        // Kuyruk ucu olmayan rollerde (Formen) ikinci istek hiç atılmaz.
        const [campaignPage, queueItems] = await Promise.all([
          fetchObtCampaigns(
            { limit: CAMPAIGN_PAGE_SIZE, offset: 0 },
            { signal: controller.signal }
          ),
          queueFetcher
            ? queueFetcher({}, { signal: controller.signal })
            : Promise.resolve(null),
        ]);

        if (controller.signal.aborted) return;

        setState({
          loading: false,
          error: null,
          campaigns: Array.isArray(campaignPage?.items) ? campaignPage.items : [],
          page: campaignPage?.page || null,
          queueItems: Array.isArray(queueItems) ? queueItems : null,
        });
      } catch (error) {
        if (isCanceled(error) || controller.signal.aborted) return;
        // Arka plan yoklaması başarısızsa mevcut paneli KORU.
        if (!silent) {
          setState({
            loading: false,
            error: normalizeObtError(error),
            campaigns: [],
            page: null,
            queueItems: null,
          });
        }
      }
    },
    [dashboardKind, enabled]
  );

  useEffect(() => {
    load();
    if (!enabled || !dashboardKind) return () => abortRef.current?.abort();
    const timer = setInterval(() => load(true), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      abortRef.current?.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, enabled, dashboardKind]);

  // Manuel "Yenile" spinner'lı yükleme (tıklama olayı `silent`'e sızmasın).
  const reload = useCallback(() => load(false), [load]);

  return { ...state, reload };
}

/** Kampanya listesinden toplam sayaç türetir (backend alan adlarıyla birebir). */
export function sumCampaignCounter(campaigns, field) {
  if (!Array.isArray(campaigns)) return 0;
  return campaigns.reduce((total, campaign) => {
    const value = campaign?.[field];
    return total + (typeof value === 'number' ? value : 0);
  }, 0);
}
